import React, { useState, useEffect, useContext } from "react";
import api from "../api/axios";
import "../styles/App.scss?v=1.1.9";
import {
  Grid,
  FormControl,
  IconButton,
  Box,
  CircularProgress,
  TextField,
  Fade,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Email,
  LockReset,
  ArrowForwardIos,
  Work,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import fidelitis2 from "../assets/fidelitis2.png";
import ChangePassword from "../Components/ChangePassword";
import { CustomButtonPlusIcon } from "../Components/Common/Design/CustomButtonPlusIcon";
import { UserContext } from "../Components/Context/Context";
import {
  buildClientSignPath,
  normalizeExpedienteId,
} from "../utils/expediente";

const Swal = require("sweetalert2");

const LoginError = ({ visible }) => {
  if (!visible) return null;

  return (
    <Box sx={{ mt: 1.5, width: "100%", maxWidth: "100%", minWidth: 0 }}>
      <Alert
        severity="error"
        sx={{
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          borderRadius: 2,
          px: 1.5,
          py: 0.75,
          overflow: "hidden",
          overflowWrap: "anywhere",
          wordBreak: "break-word",
          whiteSpace: "normal",
          "& .MuiAlert-icon": {
            display: "flex",
            alignItems: "center",
            mr: 1,
            flexShrink: 0,
          },
          "& .MuiAlert-message": {
            width: "100%",
            maxWidth: "100%",
            textAlign: "left",
            fontSize: "0.87rem",
            lineHeight: 1.45,
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          },
        }}
      >
        Datos incorrectos. Por favor, verifica tu información e intenta de nuevo.
      </Alert>
    </Box>
  );
};

function Login() {
  const [actualYear] = useState(new Date().getFullYear());
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openClientDialog, setOpenClientDialog] = useState(false);
  const [expedienteId, setExpedienteId] = useState("");
  const [expedienteError, setExpedienteError] = useState(false);
  const [bgLoaded, setBgLoaded] = useState(false);
  const [shadowVisible, setShadowVisible] = useState(false);

  const {
    fetchUserData,
    isLoggedIn,
    isLoading,
    startLoginTransition,
    finishLoginTransition,
  } = useContext(UserContext);

  const navigate = useNavigate();
  const reactLocation = useLocation();

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const handleMouseDownPassword = (event) => event.preventDefault();

  const handleOpenClientDialog = () => {
    setExpedienteId("");
    setExpedienteError(false);
    setOpenClientDialog(true);
  };

  const handleClientAccess = () => {
    const expediente = normalizeExpedienteId(expedienteId);

    if (!expediente) {
      setExpedienteError(true);
      return;
    }

    setOpenClientDialog(false);
    navigate(buildClientSignPath(expediente));
  };

  const handleLogin = async () => {
    if (!email.trim() || !password.trim() || isSubmitting) {
      if (!email.trim() || !password.trim()) {
        setLoginError(true);
      }
      return;
    }

    let inactiveUser = false;
    try {
      setIsSubmitting(true);
      setLoginError(false);
      startLoginTransition();

      const response = await api.post("/login", {
        email,
        password,
      });

      if (response.data.message === "Login Correcto") {
        if (response.data?.token) {
          sessionStorage.setItem("token", response.data.token);
        }
        await fetchUserData();
        void finishLoginTransition();
      } else {
        setLoginError(true);
        void finishLoginTransition(0);
      }
    } catch (error) {
      console.error("Error en login:", error.response);

      if (error.response?.status === 403) {
        inactiveUser = true;
        Swal.fire({
          icon: "warning",
          title: "Acceso denegado",
          text:
            error.response.data?.message ??
            "Tu cuenta está inactiva. Contacta con el administrador.",
          confirmButtonColor: "#1ab394",
          confirmButtonText: "Entendido",
        }).then((e) => {
          if (e.isConfirmed) {
            setEmail("");
            setPassword("");
            setOpenClientDialog(true);
          }
        });
      } else {
        setLoginError(true);
      }
      void finishLoginTransition(0);
    } finally {
      setIsSubmitting(false);
      if (!inactiveUser) Swal.close();
    }
  };

  useEffect(() => {
    if (!isLoading && isLoggedIn) {
      const from = reactLocation.state?.from?.pathname ?? "/panel";
      navigate(from, { replace: true });
    }
  }, [isLoading, isLoggedIn, navigate, reactLocation]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShadowVisible(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Box sx={{ width: "100%", position: "relative", height: "100vh" }}>
      {/* Fondo */}
      <Fade in={bgLoaded} timeout={1000}>
        <Box
          component="img"
          src={fidelitis2}
          alt="Fondo Login"
          onLoad={() => setBgLoaded(true)}
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: bgLoaded ? "block" : "none",
          }}
        />
      </Fade>

      {/* Contenedor principal */}
      <Fade timeout={100} in>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              maxWidth: { xs: "100%", md: 520 },
              minHeight: { xs: 540, md: 600 },
              borderRadius: "18px",
              px: { xs: 2, md: 3 },
              py: { xs: 3, md: 3.5 },
              background: { xs: "rgba(255,255,255,0.12)", md: "rgba(255,255,255,0.04)" },
              backdropFilter: { xs: "blur(2px)", md: "none" },
              boxShadow: "0 24px 80px rgba(16, 24, 40, 0.14)",
              overflow: "hidden",
              textAlign: "center",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                borderRadius: "18px",
                boxShadow: "0px 0px 220px -50px #1800b926",
                opacity: shadowVisible ? 1 : 0,
                transition: "opacity 1.5s cubic-bezier(.22,1,.36,1)",
                pointerEvents: "none",
                zIndex: -1,
              },
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Fade in timeout={2000}>
                <Box
                  sx={{
                    position: "relative",
                    display: "inline-block",
                    lineHeight: 1,
                  }}
                >
                  <Box
                    sx={{
                      fontSize: { xs: "160px", md: "198px" },
                      fontWeight: 400,
                      letterSpacing: { xs: "4px", md: "8px" },
                      color: "#12325321",
                      fontFamily: "auto !important",
                      userSelect: "none",
                    }}
                  >
                    CRM
                  </Box>

                  <Box
                    sx={{
                      position: "absolute",
                      right: { xs: "-6px", md: "-10px" },
                      bottom: { xs: "6px", md: "10px" },
                      fontSize: { xs: "38px", md: "40px" },
                      fontWeight: 500,
                      letterSpacing: "2px",
                      color: "#1c3d5ab1",
                      fontFamily: "auto !important",
                    }}
                  >
                    Legal
                  </Box>
                </Box>
              </Fade>

              <Fade in timeout={3000}>
                <Box
                  sx={{
                    mt: 1,
                    fontSize: { xs: "18px", md: "20px" },
                    color: "#6B7280",
                    fontWeight: 500,
                    letterSpacing: "1px",
                    fontFamily: "auto !important",
                  }}
                >
                  by Fidelitis
                </Box>
              </Fade>
            </Box>

            {/* Formulario */}
            <Box
              sx={{
                width: "100%",
                maxWidth: 420,
                mx: "auto",
                mt: { xs: 3, md: 3.5 },
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
              }}
            >
              <Box
                component="form"
                noValidate
                autoComplete="off"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleLogin();
                }}
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                }}
              >
                <FormControl sx={{ width: "100%", mb: 0 }}>
                  <TextField
                    color={loginError ? "error" : "primary"}
                    size="small"
                    label="Email"
                    value={email}
                    fullWidth
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleLogin();
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <IconButton edge="end">
                          <Email />
                        </IconButton>
                      ),
                    }}
                  />
                </FormControl>

                <FormControl sx={{ width: "100%", mb: 0 }}>
                  <TextField
                    size="small"
                    type={showPassword ? "text" : "password"}
                    label="Password"
                    value={password}
                    fullWidth
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleLogin();
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      ),
                    }}
                  />
                </FormControl>

                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleLogin}
                  disabled={isSubmitting}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderRadius: 2,
                    height: 42,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    mt: 0.25,
                  }}
                >
                  <Box sx={{ flex: 1, textAlign: "center" }}>
                    {isSubmitting ? (
                      <CircularProgress color="inherit" size={16} />
                    ) : (
                      "ENTRAR"
                    )}
                  </Box>
                  <ArrowForwardIos sx={{ fontSize: 16 }} />
                </Button>
              </Box>

              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 0.75,
                  "& .MuiButton-root": {
                    minWidth: { xs: "160px", md: "180px" },
                    justifyContent: "center",
                    fontSize: "0.72rem",
                    py: 0.45,
                    px: 1.2,
                  },
                }}
              >
                <CustomButtonPlusIcon
                  color="info"
                  position="start"
                  size="small"
                  variant="text"
                  icon={LockReset}
                  handleFunction={() => setOpenDialog(true)}
                  text="Cambiar contraseña"
                />

                <Button
                  color="success"
                  position="end"
                  size="small"
                  variant="text"
                  onClick={handleOpenClientDialog}
                >
                  Login cliente / Ver Expediente
                </Button>
              </Box>

              <Box
                sx={{
                  mt: 2,
                  width: "100%",
                  fontSize: "12.5px",
                  color: "#888",
                }}
              >
                CRM Legal by Fidelitis © {actualYear}
              </Box>

              <LoginError visible={loginError} />
            </Box>

            <ChangePassword
              open={openDialog}
              onClose={() => setOpenDialog(false)}
              view="login"
            />

            <Dialog
              open={openClientDialog}
              onClose={() => setOpenClientDialog(false)}
              fullWidth
              maxWidth="xs"
              PaperProps={{
                sx: {
                  borderRadius: 4,
                  overflow: "hidden",
                  border: "1px solid rgba(18, 50, 83, 0.08)",
                  boxShadow: "0 24px 70px rgba(17, 24, 39, 0.16)",
                  background:
                    "linear-gradient(180deg, #ffffff 0%, #fbfcfe 60%, #f4f7fb 100%)",
                },
              }}
            >
              <DialogTitle sx={{ px: 3, pt: 3, pb: 1.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: "14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#123253",
                      background:
                        "linear-gradient(135deg, rgba(18,50,83,0.14) 0%, rgba(18,50,83,0.05) 100%)",
                      border: "1px solid rgba(18, 50, 83, 0.08)",
                    }}
                  >
                    <Work fontSize="small" />
                  </Box>

                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "1.05rem",
                        fontWeight: 700,
                        color: "#16324f",
                        lineHeight: 1.2,
                      }}
                    >
                      Ingreso Cliente
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.35,
                        color: "#6b7280",
                        fontSize: "0.84rem",
                      }}
                    >
                      Consulta tu expediente con acceso directo y seguro.
                    </Typography>
                  </Box>
                </Box>
              </DialogTitle>
              <DialogContent sx={{ px: 3, pb: 1.5 }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    background: "rgba(255, 255, 255, 0.74)",
                    border: "1px solid rgba(18, 50, 83, 0.08)",
                    backdropFilter: "blur(8px)",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ mb: 1.25, color: "#5f6b7a", lineHeight: 1.55 }}
                  >
                    Ingresa tu número de expediente para acceder a tu información.
                  </Typography>
                  <TextField
                    autoFocus
                    margin="dense"
                    label="# Expediente"
                    fullWidth
                    size="small"
                    value={expedienteId}
                    error={expedienteError}
                    helperText={
                      expedienteError
                        ? "Ingresa un numero de expediente valido."
                        : ""
                    }
                    onChange={(e) => {
                      setExpedienteId(e.target.value);
                      if (expedienteError) setExpedienteError(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleClientAccess();
                      }
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2.5,
                        backgroundColor: "rgba(255,255,255,0.92)",
                      },
                    }}
                  />
                </Box>
              </DialogContent>
              <DialogActions
                sx={{ px: 3, pb: 3, pt: 1, justifyContent: "space-between" }}
              >
                <Button
                  onClick={() => setOpenClientDialog(false)}
                  color="error"
                  sx={{
                    borderRadius: 2.5,
                    px: 1.8,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  color="success"
                  size="small"
                  onClick={handleClientAccess}
                  variant="contained"
                  endIcon={<ArrowForwardIos sx={{ fontSize: 15 }} />}
                  sx={{
                    borderRadius: 2.5,
                    px: 2,
                    py: 0.8,
                    textTransform: "none",
                    fontWeight: 700,
                    boxShadow: "0 12px 24px rgba(34, 197, 94, 0.18)",
                  }}
                >
                  Ir a expediente
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </Box>
      </Fade>
    </Box>
  );
}

export default Login;
